import { useState, useEffect } from "react"
import { Search, Filter, Plus, Eye, Edit, MoreVertical, CheckCircle, Clock, Pause, XCircle, Play, Square, CheckSquare, Users, TrendingUp, Calendar, Building2, DollarSign, FileText, Target, BarChart3 } from "lucide-react"
import { Campaign, CampaignFilters } from "@/features/campaigns"
import { CampaignService } from "@/features/campaigns"

interface CampaignSelectorProps {
  onCampaignSelect?: (campaign: Campaign | null) => void
  selectedCampaignId?: string
  showActions?: boolean
  className?: string
  organizationId?: string
}

interface LocalFilters {
  search: string
  status: string
  organizationId: string
  page: number
  limit: number
}

interface CampaignResponse {
  data: Campaign[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export default function CampaignSelector({ 
  onCampaignSelect, 
  selectedCampaignId, 
  showActions = true,
  className = "",
  organizationId
}: CampaignSelectorProps) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  })
  const [searchInput, setSearchInput] = useState("")
  const [filters, setFilters] = useState<LocalFilters>({
    search: "",
    status: "",
    organizationId: organizationId || "",
    page: 1,
    limit: 10
  })
  const [showFilters, setShowFilters] = useState(false)

  // Fetch campaigns from API
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true)
        setError(null)
        
        const apiFilters: CampaignFilters = {
          search: filters.search || undefined,
          status: filters.status as any || undefined,
          organizationId: filters.organizationId || undefined,
          page: filters.page,
          limit: filters.limit
        }
        
        const response = await CampaignService.getCampaigns(apiFilters)
        setCampaigns(response.data || [])
        
        // Update pagination info if available
        if (response.pagination) {
          setPagination(response.pagination)
        }
      } catch (err) {
        setError("Failed to load campaigns")
        console.error("Error fetching campaigns:", err)
        setCampaigns([])
      } finally {
        setLoading(false)
      }
    }

    fetchCampaigns()
  }, [filters.search, filters.status, filters.organizationId, filters.page, filters.limit])

  // Handle search with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput, page: 1 }))
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [searchInput])

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<LocalFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 })) // Reset to first page when filtering
  }


  const handleCampaignSelect = (campaign: Campaign | null) => {
    if (onCampaignSelect) {
      onCampaignSelect(campaign)
    }
  }


  const getStatusIcon = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return <Play className="w-4 h-4 text-green-600" />
      case "INACTIVE":
        return <Pause className="w-4 h-4 text-yellow-600" />
      case "DISABLED":
        return <XCircle className="w-4 h-4 text-red-600" />
      case "HIDE":
        return <Square className="w-4 h-4 text-gray-600" />
      default:
        return <Clock className="w-4 h-4 text-gray-400" />
    }
  }

  const getStatusText = (status: string) => {
    return status.charAt(0) + status.slice(1).toLowerCase()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "text-green-600 bg-green-50"
      case "INACTIVE":
        return "text-yellow-600 bg-yellow-50"
      case "DISABLED":
        return "text-red-600 bg-red-50"
      case "HIDE":
        return "text-gray-600 bg-gray-50"
      default:
        return "text-gray-600 bg-gray-50"
    }
  }

  if (loading) {
    return (
      <div className={`card ${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`card ${className}`}>
        <div className="text-center py-8">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-accent-900 mb-2">Error Loading Campaigns</h3>
          <p className="text-accent-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`${className}`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Campaign Selection</h1>
          <p className="text-gray-600">
            {selectedCampaignId 
              ? "Campaign selected - use the sidebar to manage campaign features"
              : "Please select a campaign below to access dashboard features"
            }
          </p>
          {!selectedCampaignId && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span className="text-sm font-medium text-yellow-800">
                  Campaign selection required to continue
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Selected Campaign Display */}
        {selectedCampaignId && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <div>
                  <span className="text-sm font-medium text-green-800">Selected Campaign</span>
                  <p className="text-lg text-green-700 font-semibold">
                    {campaigns.find(c => c.id === selectedCampaignId)?.name || ''}
                  </p>
                </div>
              </div>
              <button
                onClick={() => onCampaignSelect && onCampaignSelect(null)}
                className="text-sm text-green-600 hover:text-green-800 font-medium"
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>
            <div className="flex gap-2 sm:gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base flex items-center justify-center gap-2"
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Filters</span>
              </button>
            </div>
          </div>

              {/* Advanced Filters */}
              {showFilters && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={filters.status}
                      onChange={(e) => handleFilterChange({ status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base"
                    >
                      <option value="">All Statuses</option>
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                      <option value="DISABLED">Disabled</option>
                      <option value="HIDE">Hide</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Organization
                    </label>
                    <select
                      value={filters.organizationId}
                      onChange={(e) => handleFilterChange({ organizationId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base"
                    >
                      <option value="">All Organizations</option>
                      <option value="org1">Eastern Region EM</option>
                      <option value="org2">Western Region EM</option>
                      <option value="org3">Central Region EM</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Campaign Grid - Mobile-First Responsive Layout */}
            <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4 lg:gap-6">
              {Array.from({ length: Math.max(9, campaigns.length) }, (_, index) => {
                const campaign = campaigns[index]
                const isSelected = selectedCampaignId === campaign?.id
                const isEmpty = !campaign

                return (
                  <div
                    key={index}
                    className={`rounded-xl border-2 cursor-pointer transition-all duration-200 hover:shadow-lg overflow-hidden ${
                      isEmpty
                        ? "border-dashed border-gray-300 bg-gray-50 hover:border-gray-400"
                        : isSelected
                        ? "border-red-500 bg-red-50 shadow-lg"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    } ${
                      // Mobile: full width cards, Desktop: square aspect ratio
                      "h-auto sm:aspect-square"
                    }`}
                    onClick={() => campaign && handleCampaignSelect(campaign)}
                  >
                    {campaign ? (
                      <div className="p-4 sm:p-3 lg:p-4 h-full flex flex-col overflow-hidden">
                        {/* Campaign info */}
                        <div className="h-full overflow-hidden">
                            {/* Campaign Number & Status - Mobile optimized */}
                            <div className="flex items-center justify-between mb-3 sm:mb-2 lg:mb-3">
                              <div className={`w-8 h-8 sm:w-6 sm:h-6 lg:w-8 lg:h-8 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-xs lg:text-sm ${
                                isSelected ? "bg-red-500" : "bg-green-500"
                              }`}>
                                {index + 1}
                              </div>
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                                {getStatusIcon(campaign.status)}
                                <span className="sm:hidden lg:inline">{getStatusText(campaign.status)}</span>
                              </span>
                            </div>

                            {/* Campaign Name */}
                            <h3 className="font-semibold text-gray-900 text-base sm:text-sm lg:text-base mb-2 break-words overflow-hidden" style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              wordBreak: 'break-word',
                              hyphens: 'auto'
                            }}>
                              {campaign.name}
                            </h3>

                            {/* Campaign Owner & Organization */}
                            <div className="mb-3 sm:mb-2 space-y-1">
                              {campaign.owner && (
                                <div className="flex items-center gap-1 text-sm sm:text-xs text-gray-500">
                                  <Users className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{campaign.owner.firstName} {campaign.owner.lastName}</span>
                                </div>
                              )}
                              {campaign.organization && (
                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                  <Building2 className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{campaign.organization.name}</span>
                                </div>
                              )}
                            </div>

                            {/* Campaign Stats - Clean Design */}
                            <div className="space-y-2 mb-3 sm:mb-2">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">Sales:</span>
                                <span className="font-medium text-gray-900">{campaign._count?.sales || 0}</span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">Reports:</span>
                                <span className="font-medium text-gray-900">{campaign._count?.salesReports || 0}</span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">Commission Rates:</span>
                                <span className="font-medium text-gray-900">{campaign._count?.commissionRates || 0}</span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">Override Rates:</span>
                                <span className="font-medium text-gray-900">{campaign._count?.overrideRates || 0}</span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">Intake Forms:</span>
                                <span className="font-medium text-gray-900">{campaign._count?.intakeForms || 0}</span>
                              </div>
                            </div>

                            {/* Campaign Timeline */}
                            {campaign.startDate && (
                              <div className="border-t border-gray-100 pt-2 mt-2">
                                <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                                  <Calendar className="w-3 h-3 flex-shrink-0" />
                                  <span>
                                    {new Date(campaign.startDate).toLocaleDateString()} - {campaign.endDate ? new Date(campaign.endDate).toLocaleDateString() : 'Ongoing'}
                                  </span>
                                </div>
                                <div className="text-xs text-gray-400">
                                  Created: {new Date(campaign.createdAt).toLocaleDateString()}
                                </div>
                                <div className="text-xs text-gray-400">
                                  Updated: {new Date(campaign.updatedAt).toLocaleDateString()}
                                </div>
                              </div>
                            )}
                        </div>

                      </div>
                    ) : (
                      <div className="p-4 sm:p-3 lg:p-4 h-full flex items-center justify-center min-h-[120px] sm:min-h-0">
                        <div className="text-center">
                          <Plus className="w-8 h-8 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm sm:text-xs lg:text-sm text-gray-500">Empty Slot</p>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

        {/* Results Summary and Pagination */}
        {campaigns.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-sm text-gray-600 text-center sm:text-left">
                Showing {campaigns.length} of {pagination.total} campaigns
                {(filters.search || filters.status || filters.organizationId) && " (filtered)"}
              </p>
              
              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={filters.page <= 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  <span className="text-sm text-gray-600">
                    Page {filters.page} of {pagination.totalPages}
                  </span>
                  
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, page: Math.min(pagination.totalPages, prev.page + 1) }))}
                    disabled={filters.page >= pagination.totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
